// ============================================================================
// INITIALS HELPER (TypeScript pilot — DEC-COUNCIL-010 Phase 0 frontend pilot)
// ----------------------------------------------------------------------------
// Returns the first letter of each whitespace-separated word in a string,
// joined together and upper-cased. Used by the quote code generator to build
// the RBB/<street><type><suburb> slug - a multi-word street name like
// "Lane Cove" must become "LC", not "L", so every word contributes one char.
//
// Hyphens and apostrophes are NOT splitters (regex is /\s+/ only), so
// "St Ives-on-Thames" -> ["St", "Ives-on-Thames"] -> "SI" and
// "O'Connell Street" -> ["O'Connell", "Street"] -> "OS".
//
// Unicode-safe: JavaScript `toUpperCase()` correctly handles accented chars,
// so "Saint-Etienne" -> "S" and "Etienne Leclerc Ave" -> "EL".
//
// Exists as a tiny utility module instead of being inlined three times in
// App.jsx + FullQuoteEditorModal.jsx so a future change to the algorithm
// (e.g. "always produce at least 2 letters" or "skip short particles like
// 'the' / 'of'") only needs to happen in one place.
//
// Migrated to TypeScript 2026-05-25 as the Phase 0 frontend pilot (paired
// with backend/src/utils/uuid.ts) per Minato's dual-pilot condition in
// DEC-COUNCIL-010 — exercises both the Vite and Node build chains end-to-end.
// ============================================================================

/**
 * Return the initials of every whitespace-separated word in `text`.
 *
 * @param text      The source string. Null/undefined/empty -> fallback.
 * @param fallback  What to return if `text` has no word chars. Default 'X'.
 * @returns         Upper-cased initials, or the fallback.
 */
export function initialsOf(text: string | null | undefined, fallback: string = 'X'): string {
    if (!text) return fallback;
    const letters = String(text)
        .split(/\s+/)
        .filter(Boolean)
        // A word that starts with punctuation contributes nothing. The output
        // is an identifier slug, and isResolvedRbbQuoteTitle only accepts
        // letters and digits between "RBB/" and the first space.
        //
        // Live, 2026-09-20: "7-21 Carlow Street & 2-10 Cassins Avenue, North
        // Sydney" produced "QUOTE RBB/CS&2CANS (0926)". The "&" made the title
        // fail its own validator, so a queued project was refused at Stage 4
        // with PROJECT_TITLE_UNRESOLVED after 1001 seconds of paid AI work
        // through Stages 1 to 3. Regenerating produced the same title, so the
        // operator could not retry past it either.
        .map((w) => (/^[\p{L}\p{N}]/u.test(w) ? w.charAt(0).toUpperCase() : ''))
        .join('');
    return letters || fallback;
}

/**
 * Form-data shape that `extractUnitSuffix` reads from. All fields optional —
 * the function gracefully no-ops on missing data. Kept structural so that
 * callers passing richer form objects don't need to narrow.
 */
export interface UnitSuffixSource {
    unitNumber?: string | null;
    jobDescription?: string | null;
    address?: string | null;
}

/**
 * Extract a " - UNIT X" suffix from form data if a specific unit is referenced.
 *
 * Detection sources (first match wins):
 *   1. fd.unitNumber - explicit field from intake
 *   2. fd.jobDescription text - e.g. "...wall cracks in unit 9..."
 *   3. fd.address - e.g. "Unit 9/42 Smith St, ..."
 *
 * Returns an empty string when no unit is detected, so callers can simply
 * concatenate: `${baseTitle}${extractUnitSuffix(fd)}`
 */
/**
 * Canonical comparison key over the formData fields the auto-generated quote
 * title is derived from: `address` (street/type/suburb initials) plus the
 * `extractUnitSuffix` sources (`unitNumber`, `jobDescription`, `address`).
 * Two snapshots with equal keys produce byte-identical title INPUTS — a
 * regeneration between them could only ever change the (MMYY) date suffix.
 *
 * That is exactly the drift App.jsx's title effect must avoid: opening a
 * project swaps formData wholesale, and an unconditional regeneration then
 * rewrites a stored "(0526)" title suffix to the current month, which the
 * next save persists. The effect regenerates ONLY when this key changes
 * (i.e. the user actually edited a title input); completeProjectLoad syncs
 * the reference key on load. Keep the field list in lockstep with
 * `generateQuoteCode` in App.jsx + `extractUnitSuffix` above.
 */
export function titleSourceKey(fd: UnitSuffixSource | null | undefined): string {
    return JSON.stringify([fd?.address || '', fd?.unitNumber || '', fd?.jobDescription || '']);
}

export function extractUnitSuffix(fd: UnitSuffixSource | null | undefined): string {
    if (!fd) return '';

    // 1. Explicit unitNumber field (strip leading "unit" text if user typed it)
    const explicit = (fd.unitNumber || '').trim();
    if (explicit) {
        const num = explicit.replace(/^unit\s*/i, '').trim();
        if (num) return ` - UNIT ${num.toUpperCase()}`;
    }

    // 2. Parse from objective / jobDescription text
    //    Matches "unit 9", "Unit 12A", "UNIT 3B" etc. in natural prose
    const objective = (fd.jobDescription || '').trim();
    if (objective) {
        const match = objective.match(/\bunit\s+(\d+[a-z]?)\b/i);
        // Capture group is provably defined when `match` is truthy (the regex
        // requires `\d+` so the group always matches at least one char). The
        // `!` keeps runtime byte-identical to the .js original while satisfying
        // `noUncheckedIndexedAccess` per DEC-COUNCIL-010 tsconfig.
        if (match) return ` - UNIT ${match[1]!.toUpperCase()}`;
    }

    // 3. Parse from address prefix (e.g. "Unit 9/42 Smith St")
    const addr = (fd.address || '').trim();
    if (addr) {
        const match = addr.match(/^unit\s+(\d+[a-z]?)\b/i);
        if (match) return ` - UNIT ${match[1]!.toUpperCase()}`;
    }

    return '';
}
